/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useSpring, animated } from '@react-spring/web';

import * as AppPropTypes from '../../lib/PropTypes';
import Link from '../partials/Link';
import Image from '../partials/Image';
import Detector from '../partials/Detector';
import Button from '../buttons/LinkButton';
import { getImageSize } from '../../lib/utils';

import styles from '../../../styles/items/project-card.module.scss';

const propTypes = {
    scrollDirection: PropTypes.oneOf(['left', 'right']),
    item: AppPropTypes.project.isRequired,
    rootMargin: PropTypes.string,
    className: PropTypes.string,
};

const defaultProps = {
    scrollDirection: 'right',
    rootMargin: '0%',
    className: null,
};

const ProjectCard = ({
    item: {
        title = null,
        url = null,
        parent = null,
        logo = null,
        image = null,
        plainImage = null,
        theme: { elementsColor = null },
    } = {},
    scrollDirection,
    rootMargin,
    className,
}) => {
    const [visible, setVisible] = useState(false);
    const [hovered, setHovered] = useState(false);

    const onMouseOver = useCallback(() => {
        setHovered(true);
    }, [hovered, setHovered]);

    const onMouseLeave = useCallback(() => {
        setHovered(false);
    }, [hovered, setHovered]);

    const bgRef = useRef(null);
    const [{ transform }, api] = useSpring(() => ({
        transform: `translateX(0%)`,
    }));

    const imageOffset = 10;

    useEffect(() => {
        const parallax = () => {
            const { innerHeight } = window || {};
            const { top = 0, height = 0 } =
                bgRef.current !== null ? bgRef.current.getBoundingClientRect() : {};
            const max = innerHeight > 0 ? innerHeight + height : 1;
            const y = top + height;
            const updatedValue = scrollDirection === 'left' ? 1 - y / max : y / max;
            api.start({
                transform: `translateX(${
                    Math.min(Math.max(updatedValue, 0), 1) * imageOffset - imageOffset / 2
                }%)`,
            });
        };
        if (bgRef !== null) {
            window.addEventListener('scroll', parallax);
        }
        return () => window.removeEventListener('scroll', parallax);
    }, [bgRef]);

    function getLabelBySectionType(handle) {
        let label;

        switch (handle) {
            case 'agency':
                label = (
                    <FormattedMessage defaultMessage="Agency" description="Project link label" />
                );
                break;
            case 'productions':
                label = (
                    <FormattedMessage
                        defaultMessage="Production"
                        description="Project link label"
                    />
                );
                break;
            default:
                label = (
                    <FormattedMessage
                        defaultMessage="View project"
                        description="Project link label"
                    />
                );
        }

        return label;
    }

    const hasLogo = logo !== null && plainImage !== null;
    const itemImage = hasLogo ? plainImage : image;
    const finalImage = getImageSize(itemImage, 'home');

    const { handle: sectionHandle = null } = parent || {};
    const buttonLabel = getLabelBySectionType(sectionHandle);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
            style={{ color: elementsColor }}
        >
            <div className={styles.ratioContainer}>
                <div className={styles.ratioInner}>
                    <div
                        className={classNames(styles.slidingBackgrounds, styles[scrollDirection], {
                            [styles.hovered]: hovered,
                            [styles.visible]: visible,
                        })}
                        ref={bgRef}
                    >
                        {image !== null ? (
                            <animated.img
                                src={finalImage.url}
                                className={styles.background}
                                style={{
                                    transform,
                                    width: `${100 + imageOffset}%`,
                                    left: `-${imageOffset / 2}%`,
                                }}
                                alt={title}
                            />
                        ) : null}
                    </div>
                    <Link
                        href={url}
                        className={styles.linkBox}
                        onMouseOver={onMouseOver}
                        onMouseLeave={onMouseLeave}
                    />
                    <Detector
                        className={styles.inner}
                        rootMargin={rootMargin}
                        onEnter={() => setVisible(true)}
                    >
                        <div
                            className={classNames([
                                styles.titleContainer,
                                {
                                    [styles.visible]: visible,
                                },
                            ])}
                        >
                            {hasLogo ? (
                                <Image className={styles.titleLogo} alt={title} media={logo} />
                            ) : null}
                        </div>
                        <Button
                            href={url}
                            className={classNames([
                                styles.viewProjectLink,
                                {
                                    [styles.visible]: visible,
                                    [styles.hovered]: hovered,
                                    [styles.isDark]: false, // title === 'SEXPLORA', // SERIOUSLY WTF
                                },
                            ])}
                            backgroundColor="transparent"
                        >
                            {buttonLabel}
                        </Button>
                    </Detector>
                </div>
            </div>
        </div>
    );
};

ProjectCard.propTypes = propTypes;
ProjectCard.defaultProps = defaultProps;

export default ProjectCard;
